import { useCallback, useEffect, useRef, useMemo, useState, type PropsWithChildren, type ReactNode } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2 } from 'lucide-react';
import { useEmailStore } from '@/stores/emailStore';
import { useHoverStore } from '@/stores/hoverStore';
import { useSenderGroupingStore } from '@/stores/senderGroupingStore';
import {
  SCROLL_KEY_ATTR,
  useScrollRestoration,
  type LocateResult,
  type ScrollAnchor,
} from '@/lib/scrollRestoration';

const ROW_GAP_PX = 4;
const MIN_OVERSCAN_ITEMS = 24;
const OVERSCAN_VIEWPORT_MULTIPLIER = 2;

interface EmailListVirtualizedProps<T> {
  items: T[];
  renderItem: (item: T, index: number) => ReactNode;
  estimateSize?: number;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  disableInfiniteScroll?: boolean;
  getItemKey?: (item: T, index: number) => string | number;
  children?: ReactNode;
  footer?: ReactNode;
  accountId?: string | null;
  folderId?: string | null;
  senderGroupEmail?: string | null;
  isListVisible?: boolean;
  isLoadingInitial?: boolean;
}

export function EmailListVirtualized<T>({
  items,
  renderItem,
  estimateSize = 72,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
  disableInfiniteScroll = false,
  getItemKey,
  children,
  footer,
  accountId,
  folderId,
  senderGroupEmail,
  isListVisible = true,
  isLoadingInitial = false,
}: PropsWithChildren<EmailListVirtualizedProps<T>>) {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const virtualContainerRef = useRef<HTMLDivElement | null>(null);
  const loadingRef = useRef(false);
  const isLoadingRef = useRef(isLoadingMore);
  const [overscan, setOverscan] = useState(MIN_OVERSCAN_ITEMS);

  useEffect(() => {
    isLoadingRef.current = isLoadingMore;
    loadingRef.current = isLoadingMore;
  }, [isLoadingMore]);

  // Hover hygiene: scrolling moves a DIFFERENT row under a stationary cursor,
  // but mouseenter doesn't re-fire until the mouse moves — the hover store goes
  // stale. Clear it on scroll; the next mousemove re-establishes it. (Keyboard
  // targeting additionally uses a live pointer hit-test, so this is primarily
  // for the visual hover state and the pointer-unknown fallback.)
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }
    const handleScroll = () => {
      useHoverStore.getState().clearHover();
    };
    viewport.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      viewport.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) {
      setOverscan(MIN_OVERSCAN_ITEMS);
      return;
    }

    const calculateOverscan = () => {
      const viewportHeight = viewport.clientHeight ?? 0;
      if (!viewportHeight) {
        setOverscan(MIN_OVERSCAN_ITEMS);
        return;
      }
      const estimatedRowsVisible = viewportHeight / estimateSize;
      const dynamicOverscan = Math.ceil(estimatedRowsVisible * OVERSCAN_VIEWPORT_MULTIPLIER);
      setOverscan(Math.max(MIN_OVERSCAN_ITEMS, dynamicOverscan));
    };

    calculateOverscan();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      calculateOverscan();
    });
    resizeObserver.observe(viewport);

    return () => {
      resizeObserver.disconnect();
    };
  }, [estimateSize]);

  // Pre-compute item keys once per items change to ensure stability
  // This prevents the virtualizer from getting confused when items reorder
  const itemKeys = useMemo(() => {
    const keys = new Map<number, string>();
    items.forEach((item, index) => {
      if (item != null && getItemKey) {
        const key = getItemKey(item, index);
        keys.set(index, typeof key === 'number' ? key.toString() : key);
      }
    });
    return keys;
  }, [items, getItemKey]);

  // Reverse lookup: key string -> index in current items array
  const keyToIndex = useMemo(() => {
    const map = new Map<string, number>();
    itemKeys.forEach((key, index) => {
      map.set(key, index);
    });
    return map;
  }, [itemKeys]);

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => viewportRef.current,
    estimateSize: () => estimateSize,
    getItemKey: (index) => {
      // Use pre-computed stable key, or generate one from item data
      const precomputedKey = itemKeys.get(index);
      if (precomputedKey) {
        return precomputedKey;
      }
      // For items not in the key map (shouldn't happen normally),
      // generate a key from the item if possible
      const item = items[index];
      if (item && getItemKey) {
        const key = getItemKey(item, index);
        return typeof key === 'number' ? key.toString() : key;
      }
      // Last resort: use index, but mark it clearly as temporary
      // This helps identify rendering issues during development
      return `__temp_${index}_${items.length}`;
    },
    overscan,
  });

  const virtualItems = virtualizer.getVirtualItems();

  // Filter out virtual items that don't have corresponding data
  // This prevents blank rows when items array shrinks before virtualizer recalculates
  const safeVirtualItems = useMemo(() => {
    return virtualItems.filter((virtualItem) => {
      if (virtualItem.index >= items.length) {
        return false;
      }
      const item = items[virtualItem.index];
      if (item == null) {
        return false;
      }
      // Also skip items without a stable key - they'll cause rendering issues
      if (getItemKey && !itemKeys.has(virtualItem.index)) {
        return false;
      }
      return true;
    });
  }, [virtualItems, items, itemKeys, getItemKey]);

  // Infinite scroll detection
  // Include isLoadingMore in dependencies so the effect re-runs when loading completes.
  // This ensures we re-check intersection after data loads - important when the sentinel
  // is already visible (e.g., archive folder with one large sender group).
  useEffect(() => {
    if (!onLoadMore || !hasMore || disableInfiniteScroll) {
      return;
    }
    // Don't set up observer while loading - wait for loading to complete
    if (isLoadingMore) {
      return;
    }
    const viewport = viewportRef.current;
    const sentinel = loadMoreRef.current;
    if (!viewport || !sentinel) {
      return;
    }

    // Reset loading ref when setting up a new observer after loading completes
    // This allows subsequent infinite scroll triggers to work
    loadingRef.current = false;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting && !loadingRef.current) {
          loadingRef.current = true;
          onLoadMore();
          // Several onLoadMore implementations no-op synchronously (guards,
          // missing context) without ever flipping a loading flag — which is
          // what re-runs this effect and resets loadingRef. Unwedge after a
          // beat if no load actually started (A11).
          window.setTimeout(() => {
            if (!isLoadingRef.current) {
              loadingRef.current = false;
            }
          }, 500);
        }
      },
      {
        root: viewport,
        rootMargin: '1000px',
        threshold: 0.1,
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [disableInfiniteScroll, hasMore, onLoadMore, isLoadingMore]);

  // While the reader is open, MailboxPage FREEZES the list's element (P12), so
  // the isListVisible PROP never transitions across an open/close cycle — the
  // frozen element was captured while visible, and the live element on return
  // is visible again. Derive visibility from the stores instead: zustand
  // subscriptions fire in this component regardless of the parent's frozen
  // element, so "reader closed" is observable and re-arms scroll restoration.
  const detailOpen = useEmailStore(
    (s) => Boolean(s.selectedEmailId || s.selectedThreadId)
  );
  const digestVisible = useSenderGroupingStore((s) => s.senderDigestVisible);
  const listVisibleLive = isListVisible && !detailOpen && !digestVisible;

  // Scroll restoration (see lib/scrollRestoration). Rows carry their item key
  // as `data-scroll-key`; the shared hook measures the DOM for capture and
  // restore. The only virtualization-specific piece is `locate`: when the
  // anchor row isn't rendered, jump near it using the virtualizer's measured
  // (or estimated) offsets so it renders, and let the hook fine-tune from the
  // real element. Rows above it that were only estimated get measured as they
  // render; the virtualizer compensates for those size changes itself and the
  // hook's settle pass verifies the final position from the DOM.
  const scopeKey =
    accountId && folderId
      ? senderGroupEmail
        ? `mail:${accountId}:${folderId}:sender:${senderGroupEmail}`
        : `mail:${accountId}:${folderId}`
      : null;

  const keyToIndexRef = useRef(keyToIndex);
  keyToIndexRef.current = keyToIndex;
  const virtualizerRef = useRef(virtualizer);
  virtualizerRef.current = virtualizer;
  const isLoadingInitialRef = useRef(isLoadingInitial);
  isLoadingInitialRef.current = isLoadingInitial;
  const loadMoreStateRef = useRef<{ hasMore: boolean; onLoadMore?: () => void; disabled: boolean }>({
    hasMore,
    onLoadMore,
    disabled: disableInfiniteScroll,
  });
  loadMoreStateRef.current = { hasMore, onLoadMore, disabled: disableInfiniteScroll };

  const locate = useCallback(
    (anchor: ScrollAnchor): LocateResult => {
      const candidates = anchor.key ? [anchor.key, ...anchor.neighbors] : [];
      let index: number | undefined;
      for (const candidate of candidates) {
        const found = keyToIndexRef.current.get(candidate);
        if (found !== undefined) {
          index = found;
          break;
        }
      }
      // A remount or folder switch only brings back the first page; the
      // anchor may sit in a later page the user had scrolled to, or be the
      // last loaded row with nothing below it to scroll against. Pull pages
      // in until it can be placed (or the list ends) — exactly what scrolling
      // back down would have done.
      const requestMore = (): LocateResult => {
        if (isLoadingInitialRef.current || isLoadingRef.current) return 'pending';
        const { hasMore: more, onLoadMore: loadMore, disabled } = loadMoreStateRef.current;
        if (!more || !loadMore || disabled) return 'missing';
        if (!loadingRef.current) {
          loadingRef.current = true;
          loadMore();
        }
        return 'pending';
      };
      if (index === undefined) return requestMore();
      const viewport = viewportRef.current;
      const container = virtualContainerRef.current;
      if (!viewport || !container) return 'pending';
      // Container's top in scroll-content coordinates (decorators render above it).
      const containerTop =
        container.getBoundingClientRect().top - viewport.getBoundingClientRect().top + viewport.scrollTop;
      // measurementsCache is refreshed by every getVirtualItems()/getTotalSize()
      // call, i.e. on every render — unlike getOffsetForIndex it isn't clamped
      // to the content currently loaded, which is exactly the signal needed to
      // know whether more pages are required.
      const start = virtualizerRef.current.measurementsCache[index]?.start ?? index * estimateSize;
      const target = containerTop + start - anchor.offset;
      viewport.scrollTop = target;
      // Target beyond what the loaded content allows → more pages needed.
      if (target > viewport.scrollHeight - viewport.clientHeight + 1) {
        const more = requestMore();
        return more === 'missing' ? 'located' : more;
      }
      return 'located';
    },
    [estimateSize],
  );

  useScrollRestoration({
    scopeKey,
    viewportRef,
    active: listVisibleLive,
    ready: items.length > 0 && !isLoadingInitial && !isLoadingMore,
    locate,
  });

  return (
    <ScrollArea className="flex-1 w-full min-w-0 overflow-hidden pb-0 md:pb-4" viewportRef={viewportRef}>
      <div className="w-full px-3 pb-4 pt-2">
        {children && <div className="space-y-1">{children}</div>}
        <div
          ref={virtualContainerRef}
          className="relative"
          style={{
            height: `${virtualizer.getTotalSize()}px`,
            width: '100%',
          }}
        >
          {safeVirtualItems.map((virtualItem) => {
            const item = items[virtualItem.index];

            return (
              <div
                key={virtualItem.key}
                ref={virtualizer.measureElement}
                data-index={virtualItem.index}
                {...{ [SCROLL_KEY_ATTR]: String(virtualItem.key) }}
                className="absolute left-0 top-0 w-full"
                style={{
                  transform: `translateY(${virtualItem.start}px)`,
                  paddingBottom: `${ROW_GAP_PX}px`,
                }}
              >
                {renderItem(item, virtualItem.index)}
              </div>
            );
          })}
        </div>
        {hasMore && <div ref={loadMoreRef} className="h-4 w-full" />}
        {isLoadingMore && (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          </div>
        )}
        {footer && <div className="mt-3 space-y-2">{footer}</div>}
      </div>
    </ScrollArea>
  );
}
